import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from './api.service';
import { countries as CountriesConst, Mappingcodes } from '../constants/currencies.contstant';

export interface DetectedLocale {
  countryCode: string;
  countryName: string;
  currencyCode: string;
  currencySymbol: string;
}

const LOCALE_KEYS = {
  countryCode: 'selected_country_code',
  countryName: 'selected_country_name',
  currencyCode: 'selected_currency_code',
  currencySymbol: 'selected_currency_symbol',
  manuallySelected: 'locale_manually_selected',
} as const;

function getSessionStorage(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

export function getSelectedCountryCode(): string {
  return getSessionStorage()?.getItem(LOCALE_KEYS.countryCode) || 'IN';
}

export function getSelectedCountryName(): string {
  return getSessionStorage()?.getItem(LOCALE_KEYS.countryName) || 'India';
}

export function getSelectedCurrencyCode(): string {
  return getSessionStorage()?.getItem(LOCALE_KEYS.currencyCode) || 'INR';
}

export function getSelectedCurrencySymbol(): string {
  return getSessionStorage()?.getItem(LOCALE_KEYS.currencySymbol) || '₹';
}

/**
 * Detects the visitor's country via IP geolocation and sets the default
 * currency/country for the current browser session. A manual footer selection
 * is respected until the tab is closed; otherwise IP detection runs on each load.
 */
@Injectable({ providedIn: 'root' })
export class LocaleCurrencyService {
  private readonly apiService = inject(ApiService);

  /** Emits whenever the locale is updated from IP detection so views can refresh. */
  readonly localeChanged$ = new Subject<DetectedLocale>();

  private readonly IPIFY_ENDPOINT = 'https://api.ipify.org?format=json';

  /** Runs IP-based detection unless the visitor manually selected a locale this session. */
  initLocaleFromIp(): void {
    if (this.hasManualLocaleSelection()) {
      return;
    }

    // Use fetch (not HttpClient) so the auth interceptor doesn't attach
    // Authorization/credentials to this cross-origin request.
    fetch(this.IPIFY_ENDPOINT)
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { ip?: string } | null) => {
        const ipAddress = data?.ip?.trim();
        if (!ipAddress) {
          return;
        }

        this.apiService
          .ipGeoLookup(ipAddress)
          .pipe(take(1))
          .subscribe({
            next: (geo) => {
              const countryCode = (geo?.location?.country || '').toUpperCase();
              if (countryCode) {
                this.applyCountryCode(countryCode);
              }
            },
            error: () => {
              // Detection is best-effort; fall back to existing defaults silently.
            },
          });
      })
      .catch(() => {
        // Detection is best-effort; fall back to existing defaults silently.
      });
  }

  /** Maps a country code to the app's currency and persists the locale. */
  applyCountryCode(countryCode: string): void {
    const currencyCode = (Mappingcodes as Record<string, string>)[countryCode];
    if (!currencyCode) {
      return;
    }

    const country = (CountriesConst?.LIST || []).find((c: any) => c.code === countryCode);
    const countryName = country?.en_short_name || '';

    this.apiService
      .getCurrencies()
      .pipe(take(1))
      .subscribe({
        next: (currencies: any[]) => {
          const match = (currencies || []).find((c: any) => c.code === currencyCode);
          this.persistLocale({
            countryCode,
            countryName,
            currencyCode,
            currencySymbol: match?.symbol || '',
          });
        },
        error: () => {
          this.persistLocale({ countryCode, countryName, currencyCode, currencySymbol: '' });
        },
      });
  }

  /** Persists a manual footer selection for the current session only. */
  persistManualLocale(locale: DetectedLocale): void {
    this.persistLocale(locale);
    const storage = getSessionStorage();
    storage?.setItem(LOCALE_KEYS.manuallySelected, 'true');
  }

  private hasManualLocaleSelection(): boolean {
    return getSessionStorage()?.getItem(LOCALE_KEYS.manuallySelected) === 'true';
  }

  private persistLocale(locale: DetectedLocale): void {
    const storage = getSessionStorage();
    if (storage) {
      storage.setItem(LOCALE_KEYS.countryCode, locale.countryCode);
      if (locale.countryName) {
        storage.setItem(LOCALE_KEYS.countryName, locale.countryName);
      }
      storage.setItem(LOCALE_KEYS.currencyCode, locale.currencyCode);
      if (locale.currencySymbol) {
        storage.setItem(LOCALE_KEYS.currencySymbol, locale.currencySymbol);
      }
    }

    this.localeChanged$.next(locale);
  }
}
